import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Icon } from '@repo/ui';

interface BreadcrumbItem {
  label: string;
  path: string;
}

/**
 * Breadcrumbs component that automatically generates breadcrumbs from the current route
 *
 * @example
 * // For route: /reports/ar
 * // Renders: Home > Reports > Accounts Receivable Exception
 */
export function Breadcrumbs() {
  const location = useLocation();

  // Route label mapping
  const routeLabels: Record<string, string> = {
    'juniper-transactions': 'Transactions',
    'juniper-config': 'Juniper Configuration',
    'template-manager': 'Template Manager',
    'webhooks': 'Clio Webhooks',
    'webhook-activity': 'Clio Webhook Activity',
    'qbo-webhooks': 'QBO Webhooks',
    'qbo-webhook-activity': 'QBO Webhook Activity',
    'archival': 'Archival',
    'reports': 'Reports',
    'ar': 'Accounts Receivable Exception',
    'trust': '3-Way Trust Reconciliation',
    'accounts-receivable-exception': 'Accounts Receivable Exception',
    'revenue-report': 'Revenue Report',
    'aging-report': 'Aging Report',
    'settings': 'Settings',
    'profile': 'Profile',
  };

  // Generate breadcrumb items from pathname
  const generateBreadcrumbs = (): BreadcrumbItem[] => {
    const paths = location.pathname.split('/').filter(Boolean);
    const breadcrumbs: BreadcrumbItem[] = [{ label: 'Home', path: '/' }];

    let currentPath = '';
    paths.forEach(segment => {
      currentPath += `/${segment}`;
      const label =
        routeLabels[segment] ||
        segment
          .split('-')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');

      breadcrumbs.push({
        label,
        path: currentPath,
      });
    });

    return breadcrumbs;
  };

  const breadcrumbs = generateBreadcrumbs();

  // Don't show breadcrumbs on home page
  if (breadcrumbs.length <= 1) {
    return null;
  }

  return (
    <nav className="flex items-center space-x-2 text-sm" aria-label="Breadcrumb">
      {breadcrumbs.map((crumb, index) => {
        const isLast = index === breadcrumbs.length - 1;

        return (
          <React.Fragment key={crumb.path}>
            {index > 0 && (
              <Icon name="HiChevronRight" size="xs" className="text-xtnd-dark-300 dark:text-xtnd-dark-400" />
            )}
            {isLast ? (
              <span className="font-medium text-xtnd-dark dark:text-xtnd-white" aria-current="page">
                {crumb.label}
              </span>
            ) : (
              <Link
                to={crumb.path}
                className="text-xtnd-dark-500 dark:text-xtnd-dark-300 hover:text-xtnd-blue dark:hover:text-xtnd-blue transition-colors"
              >
                {crumb.label}
              </Link>
            )}
          </React.Fragment>
        );
      })}
    </nav>
  );
}
